import React from "react";
import { ShieldCheck, CheckCircle2, Award } from "lucide-react";

export function Card({ className = "", children, ...props }) {
  return (
    <div
      className={`bg-carbon-800 border border-carbon-600 rounded-xl overflow-hidden ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

export function StatusDot({ status = "available" }) {
  const map = {
    available: "bg-status-available shadow-[0_0_8px_rgba(34,197,94,0.65)]",
    limited: "bg-status-limited shadow-[0_0_8px_rgba(245,158,11,0.6)]",
    unavailable: "bg-status-unavailable shadow-[0_0_8px_rgba(239,68,68,0.6)]",
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${map[status]}`} />;
}

export function Chip({ children, className = "", ...props }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-carbon-750 border border-carbon-600 text-ink-800 ${className}`}
      {...props}
    >
      {children}
    </span>
  );
}

/** MatchBadge — deterministic capability match indicator.
 *  Levels: 'direct' | 'partial' | 'none'. Renders a small pill with a tooltip listing
 *  matched vs missing capabilities. Purely presentational — does NOT hide anything. */
export function MatchBadge({ level = "none", matched = [], missing = [], reason = "", className = "" }) {
  const cfg = {
    direct:  { label: "Direct match",  cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
    partial: { label: "Partial match", cls: "text-electric-400 border-electric-500/40 bg-electric-500/10" },
    none:    { label: "No match",      cls: "text-ink-600 border-carbon-600 bg-carbon-750" },
  }[level] || { label: "No match", cls: "text-ink-600 border-carbon-600 bg-carbon-750" };
  const title =
    reason === "no-capabilities" ? "Complete your capabilities in your profile to enable matching" :
    reason === "no-requirements" ? "This request has no structured requirements" :
    (matched.length ? `Matched: ${matched.join(", ")}` : "") +
    (matched.length && missing.length ? "  ·  " : "") +
    (missing.length ? `Missing: ${missing.join(", ")}` : "");
  return (
    <div title={title || undefined}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border font-mono text-[10px] uppercase tracking-widest ${cfg.cls} ${className}`}
      data-testid={`match-badge-${level}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      <span>{cfg.label}</span>
    </div>
  );
}

export function CompatibilityBadge({ score, className = "", size = "md" }) {
  const sizes = { md: "px-2 py-1 text-xs", lg: "px-3 py-1.5 text-sm" };
  if (score == null) {
    return (
      <div className={`inline-flex items-center gap-1.5 rounded-md border font-mono text-ink-600 border-carbon-600 bg-carbon-700 ${sizes[size]} ${className}`} title="Complete your profile to calculate compatibility">
        <span className="w-1.5 h-1.5 rounded-full bg-ink-500" />
        <span className="tracking-wide">—</span>
      </div>
    );
  }
  const color = score >= 90 ? "text-electric-400 border-electric-500/40 bg-electric-500/10"
    : score >= 75 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
    : "text-ink-700 border-carbon-600 bg-carbon-700";
  return (
    <div className={`inline-flex items-center gap-1.5 rounded-md border font-mono ${color} ${sizes[size]} ${className}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      <span className="tracking-wide">{score}%</span>
    </div>
  );
}

/** Ring / radial score visualization */
export function CompatibilityRing({ score = null, size = 72, stroke = 6 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const isReady = score != null;
  const offset = circumference - ((score || 0) / 100) * circumference;
  const color = !isReady ? "#B5AF95" : score >= 90 ? "#2E6BFF" : score >= 75 ? "#22C55E" : "#7481A0";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} title={!isReady ? "Compatibility available after profile completion" : undefined}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E1D8BE" strokeWidth={stroke} fill="none" />
        {isReady && (
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-mono font-semibold text-ink-900" style={{ fontSize: size * (isReady ? 0.28 : 0.34) }}>{isReady ? score : "—"}</span>
        <span className="text-[9px] font-mono text-ink-600 tracking-widest">MATCH</span>
      </div>
    </div>
  );
}

/** Detailed match reasons — the transparency element */
export function MatchScoreBreakdown({ reasons = [], className = "" }) {
  return (
    <div className={`space-y-2.5 ${className}`}>
      {reasons.map((r) => (
        <div key={r.label} className="flex items-center gap-3">
          <div className="w-28 shrink-0 text-[10px] font-mono uppercase tracking-wider text-ink-600">
            {r.label}
          </div>
          <div className="flex-1 h-1.5 rounded-full bg-carbon-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-electric-500 to-electric-600"
              style={{ width: `${r.score}%`, transition: "width 0.9s ease-out" }}
            />
          </div>
          <div className="w-10 text-right font-mono text-xs text-ink-900">{r.score}%</div>
        </div>
      ))}
    </div>
  );
}

/** Reputation badge — small pill with amber tint that scales with reputation.
 *  Reputation is DISTINCT from Match Score: earned over time, not per-request.
 *  When there is no reputation yet (0 completed collaborations), render a neutral
 *  "New member" pill so we never invent fake ratings. */
export function ReputationBadge({ score = 0, label, className = "" }) {
  if (!score || score <= 0) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-ink-600 border-carbon-600 bg-carbon-750 ${className}`} title="No completed collaborations yet">
        <Award className="w-3 h-3" />
        <span className="font-mono text-[10px] uppercase tracking-widest">New member</span>
      </div>
    );
  }
  const level =
    score >= 90 ? { tone: "text-[#8B5A0C] border-[#C88A1A]/60 bg-[#F5B93B]/25", key: "expert" } :
    score >= 75 ? { tone: "text-[#8B5A0C] border-[#C88A1A]/40 bg-[#F5B93B]/15", key: "established" } :
    score >= 60 ? { tone: "text-ink-800 border-carbon-500 bg-carbon-750", key: "rising" } :
                  { tone: "text-ink-700 border-carbon-600 bg-carbon-750", key: "new" };
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${level.tone} ${className}`}>
      <Award className="w-3 h-3" />
      <span className="font-mono text-[11px] tracking-wide">{score}</span>
      {label && <span className="text-[10px] uppercase tracking-widest">{label}</span>}
    </div>
  );
}

/** Sidebar / nav counter badge */
export function NavBadge({ count = 0, tone = "electric", className = "" }) {
  if (!count || count < 1) return null;
  const map = {
    electric: "bg-electric-500 text-ink-900",
    amber: "bg-[#F5B93B] text-carbon-950",
    muted: "bg-carbon-600 text-ink-800",
  };
  return (
    <span data-testid="nav-badge" className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full font-mono text-[10px] font-semibold ${map[tone]} ${className}`}>
      {count > 99 ? "99+" : count}
    </span>
  );
}

/** Simple "NEW" pulse — small dot with electric pulse animation */
export function LivePulse({ label = "NEW", className = "" }) {
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className="relative flex w-2 h-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-electric-500/70 animate-ping" />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-electric-500" />
      </span>
      <span className="font-mono text-[10px] uppercase tracking-widest text-electric-400">{label}</span>
    </span>
  );
}

/** Trust indicator row */
export function TrustPill({ icon: Icon = CheckCircle2, label, value }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-carbon-800 border border-carbon-600">
      <Icon className="w-3.5 h-3.5 text-electric-500" />
      <div className="flex items-baseline gap-1.5">
        <span className="text-[10px] font-mono uppercase tracking-wider text-ink-600">{label}</span>
        <span className="text-xs font-semibold text-ink-900">{value}</span>
      </div>
    </div>
  );
}

export function VerifiedBadge({ label = "Verified" }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-electric-500/15 border border-electric-500/40 text-electric-400 text-[10px] font-mono uppercase tracking-wider">
      <ShieldCheck className="w-3 h-3" /> {label}
    </span>
  );
}

/** Full-page engineering blueprint background — opacity driven by theme --bp-opacity var */
export function BlueprintBg({ opacity }) {
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' width='240' height='240' viewBox='0 0 240 240'>
      <defs>
        <pattern id='minor' width='20' height='20' patternUnits='userSpaceOnUse'>
          <path d='M20 0 L0 0 0 20' fill='none' stroke='#2E6BFF' stroke-width='0.5' opacity='1'/>
        </pattern>
        <pattern id='major' width='120' height='120' patternUnits='userSpaceOnUse'>
          <rect width='120' height='120' fill='url(#minor)'/>
          <path d='M120 0 L0 0 0 120' fill='none' stroke='#2E6BFF' stroke-width='0.9' opacity='1'/>
        </pattern>
      </defs>
      <rect width='240' height='240' fill='url(#major)'/>
      <g stroke='#2E6BFF' stroke-width='0.7' opacity='0.9' fill='none'>
        <circle cx='60' cy='60' r='3'/>
        <path d='M60 40 L60 80 M40 60 L80 60'/>
        <circle cx='180' cy='180' r='14'/>
        <circle cx='180' cy='180' r='28' stroke-dasharray='2 4'/>
        <path d='M20 200 L220 200' stroke-dasharray='3 6'/>
      </g>
    </svg>`.trim();
  const encoded = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  const style = {
    backgroundImage: `url("${encoded}")`,
    backgroundRepeat: "repeat",
    maskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
    WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 90%)",
    // If opacity is passed explicitly, use it; otherwise inherit from theme (multiplier).
    opacity: opacity !== undefined ? opacity : "calc(var(--bp-opacity, 0.05) * 10)",
  };
  return <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={style} />;
}

export function PageHeader({ title, subtitle, right, testId, kicker }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6" data-testid={testId}>
      <div>
        {kicker && (
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-electric-400 mb-1.5">
            {kicker}
          </p>
        )}
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">{title}</h1>
        {subtitle && <p className="text-sm text-ink-600 mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Input({ className = "", ...props }) {
  return (
    <input
      className={`w-full bg-carbon-800 border border-carbon-600 rounded-lg px-3.5 py-2.5 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-electric-500 focus:ring-1 focus:ring-electric-500/40 transition ${className}`}
      {...props}
    />
  );
}

export function Textarea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full bg-carbon-800 border border-carbon-600 rounded-lg px-3.5 py-2.5 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-electric-500 focus:ring-1 focus:ring-electric-500/40 transition min-h-[120px] resize-none ${className}`}
      {...props}
    />
  );
}

export function Button({ variant = "primary", className = "", children, ...props }) {
  const styles = {
    primary: "bg-electric-500 hover:bg-electric-400 text-ink-900 shadow-[0_0_20px_rgba(46,107,255,0.28)]",
    secondary: "bg-carbon-700 hover:bg-carbon-650 text-ink-900 border border-carbon-600",
    ghost: "hover:bg-carbon-800 text-ink-800 hover:text-ink-900",
    outline: "border border-carbon-600 hover:border-electric-500 text-ink-900 hover:text-electric-400",
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${styles[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Label({ children, htmlFor, className = "" }) {
  return (
    <label htmlFor={htmlFor} className={`block text-xs font-medium text-ink-700 mb-1.5 tracking-wide ${className}`}>
      {children}
    </label>
  );
}

/** Toggle switch — for the Match Score "Analyze compatibility" option */
export function Toggle({ checked = false, onChange, label, help, className = "", testId }) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer ${className}`} data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange?.(!checked)}
        className={`relative shrink-0 w-9 h-5 rounded-full transition-colors mt-0.5 ${
          checked ? "bg-electric-500" : "bg-carbon-700 border border-carbon-600"
        }`}
        aria-pressed={checked}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-transform ${
            checked ? "translate-x-4" : "translate-x-0"
          }`}
        />
      </button>
      <div className="min-w-0">
        {label && <div className="text-sm text-ink-900 font-medium">{label}</div>}
        {help && <div className="text-[11px] text-ink-600 mt-0.5 leading-snug">{help}</div>}
      </div>
    </label>
  );
}
